'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { type Components, Virtuoso } from 'react-virtuoso';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logExploreTrailCardListActionTaken from '@alltrails/amplitude/events/logExploreTrailCardListActionTaken';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ExploreSeasonalMonthSelector from '@alltrails/explore-service/components/ExploreSeasonalMonthSelector/ExploreSeasonalMonthSelector';
import useExploreServiceFilterCounts from '@alltrails/explore-service/hooks/useExploreServiceFilterCounts';
import useGeneralExploreServiceQueryArgs from '@alltrails/explore-service/hooks/useGeneralExploreServiceQueryArgs';
import { useIsSeasonalSort } from '@alltrails/explore-service/hooks/useIsSeasonalSort';
import type { ExploreServiceSearchResult } from '@alltrails/explore-service/types/ExploreServiceResponse';
import { getCurrentMonth } from '@alltrails/explore-service/utils/seasonalMonth';
import PlanATripCard from '@alltrails/layout/components/PlanATripCard/PlanATripCard';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { useSidebarContext } from '@alltrails/maps/components/Sidebar/SidebarContext';
import { useSidebar } from '@alltrails/maps/components/Sidebar/useSidebar';
import { updateActiveTrails, updateHoveredPOI } from '@alltrails/maps/redux/reducer';
import NoResults from '@/components/NoResults/NoResults';
import POIResultCard from '@/components/POIResultCard/POIResultCard';
import SidebarLoading from '@/components/SidebarLoading/SidebarLoading';
import SortDropdown from '@/components/SortDropdown/SortDropdown';
import useExpandSearch from '@/hooks/useExpandSearch';
import useIsTripPlannerAvailableForSearchArea from '@/hooks/useIsTripPlannerAvailableForSearchArea';
import { useOnNewBoundingBox } from '@/hooks/useOnNewBoundingBox';
import useShouldFetchParks from '@/hooks/useShouldFetchParks';
import { explorePath } from '@/utils/constants';
import { useLogPOISelected } from '@/utils/poiAnalyticsHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsMobileListAtTop } from '@/utils/slices/exploreSlice';
import ListViewTrailCard from '../ListViewTrailCard/ListViewTrailCard';
import styles from './ExploreRootSidebar.module.scss';

const NaturalLanguageSearchSummary = dynamic(() => import('@/components/NaturalLanguageSearchSummary/NaturalLanguageSearchSummary'), {
  ssr: false
});

type NaturalLanguageSearchSummaryResult = { type: 'natural-language-search-summary' };
const naturalLanguageSearchSummaryResult: NaturalLanguageSearchSummaryResult = { type: 'natural-language-search-summary' };

const analyticsInfo = { detailedCardLocation: 'explore tab list view' as CardLocation, page: PageStrings.EXPLORE_ALL_PAGE };

const ExploreSidebar = () => {
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useAppDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const searchParams = useSearchParams();
  const searchInfoRef = useRef<HTMLDivElement>(null);

  const { isComplete, isFavorite, isVerified, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(undefined, undefined, analyticsInfo);
  const onNewBoundingBox = useOnNewBoundingBox();
  const shouldFetchParks = useShouldFetchParks();
  const exploreQueryArgs = useGeneralExploreServiceQueryArgs({ hasTrails: true, shouldFetchParks });
  const expandSearch = useExpandSearch(exploreQueryArgs, onNewBoundingBox);

  const { naturalLanguageQuery, naturalLanguageSearchSummary, searchResults, searchIsLoading, searchResultsSummary, seasonalMonth, skipQuery } =
    useAppSelector(state => ({
      naturalLanguageQuery: state.exploreService.naturalLanguageQuery,
      naturalLanguageSearchSummary: state.exploreService.naturalLanguageSearchSummary,
      searchResults: state.exploreService.searchResults,
      searchIsLoading: state.exploreService.searchIsLoading,
      searchResultsSummary: state.exploreService.searchResultsSummary,
      seasonalMonth: state.exploreService.seasonalMonth || getCurrentMonth(),
      skipQuery: state.exploreService.skipQuery
    }));
  const { adminFilterCount, filterCount } = useExploreServiceFilterCounts();

  const isTripPlannerEnabled = useIsTripPlannerAvailableForSearchArea();

  // `skipQuery` is true while the search is deferred during a transition to a searched location (the map is
  // panning to the destination). Treat that as loading so we show a spinner instead of flashing the empty state.
  const isLoading = Boolean(searchIsLoading) || skipQuery;

  const resultsWithPhotos = useMemo(() => {
    let renderedResults: (ExploreServiceSearchResult | NaturalLanguageSearchSummaryResult)[] = searchResults;

    if (!searchIsLoading && naturalLanguageQuery) {
      renderedResults = [naturalLanguageSearchSummaryResult, ...searchResults];
    }

    const hasFilters = Boolean(adminFilterCount || filterCount);

    return renderedResults.filter(result => {
      // Hide POIs when filters are applied
      if (hasFilters && result.type === 'poi') {
        return false;
      }
      // We don't display parks in the sidebar, so don't count them as results
      if (result.type === 'area') {
        return false;
      }
      // Otherwise, only show POIs that have photos
      if (result.type === 'poi') {
        return (result.photos_count ?? 0) > 0;
      }
      return true;
    });
  }, [searchResults, naturalLanguageQuery, searchIsLoading, adminFilterCount, filterCount]);

  const logPOISelected = useLogPOISelected();
  const isSeasonalSort = useIsSeasonalSort();

  const [isAtTop, setIsAtTop] = useState(true);
  const { SidebarHeader, SidebarContent } = useSidebar({
    collapseBehavior: 'full',
    preventHeightRecalculation: isLoading,
    onBackRequest: undefined
  });

  const onMouseEnterResult = useCallback(
    (result: ExploreServiceSearchResult) => {
      switch (result.type) {
        case 'poi':
          dispatch(updateHoveredPOI(result));
          break;
        case 'trail':
          dispatch(updateActiveTrails([result]));
          break;
      }
    },
    [dispatch]
  );

  const onMouseLeaveResult = useCallback(() => {
    dispatch(updateHoveredPOI(undefined));
    dispatch(updateActiveTrails(undefined));
  }, [dispatch]);

  const renderResultCard = useCallback(
    (index: number, result: ExploreServiceSearchResult | NaturalLanguageSearchSummaryResult) => {
      switch (result.type) {
        case 'poi':
          const poiUrl = wrapUrlSafe(`${explorePath}/${result.slug}?${searchParams}`, languageRegionCode);
          return (
            <POIResultCard
              key={result.ID}
              href={poiUrl}
              index={index}
              onClick={poi => logPOISelected(poi, 'explore map poi card')}
              onMouseEnter={onMouseEnterResult}
              onMouseLeave={onMouseLeaveResult}
              poi={result}
            />
          );
        case 'trail':
          return (
            <ListViewTrailCard
              key={result.ID}
              handleFavoriteClick={handleFavoriteClick}
              isComplete={isComplete}
              isFavorite={isFavorite}
              isVerified={isVerified}
              onCardMouseEnter={() => onMouseEnterResult(result)}
              onCardMouseLeave={onMouseLeaveResult}
              index={index}
              trail={result}
            />
          );
        case 'natural-language-search-summary':
          return naturalLanguageQuery ? (
            <NaturalLanguageSearchSummary
              key="natural-language-search-summary"
              naturalLanguageQuery={naturalLanguageQuery}
              summaryHtml={naturalLanguageSearchSummary}
            />
          ) : null;
        default:
          return null;
      }
    },
    [
      handleFavoriteClick,
      isComplete,
      isFavorite,
      isVerified,
      languageRegionCode,
      logPOISelected,
      naturalLanguageQuery,
      naturalLanguageSearchSummary,
      onMouseEnterResult,
      onMouseLeaveResult,
      searchParams
    ]
  );

  const [hasScrolled, setHasSrolled] = useState(false);
  const onScroll = useMemo(() => {
    if (hasScrolled) {
      return undefined;
    }
    return () => {
      logExploreTrailCardListActionTaken({ action_taken: 'scrolled' });
      setHasSrolled(true);
    };
  }, [hasScrolled]);

  const NullState = useMemo(
    () => (
      <NoResults
        className={styles.noResults}
        expandable={!!searchResultsSummary?.expandable}
        hasFilters={Boolean(adminFilterCount || filterCount)}
        onZoomOutClick={expandSearch}
      />
    ),
    [adminFilterCount, expandSearch, filterCount, searchResultsSummary?.expandable]
  );

  const mobilePlanATripCard = useMemo(
    () => (isTripPlannerEnabled ? <PlanATripCard className={styles.mobilePlanATrip} entryPoint="explore side bar" /> : null),
    [isTripPlannerEnabled]
  );

  const mobileVirtuosoComponents = useMemo<Components<ExploreServiceSearchResult | NaturalLanguageSearchSummaryResult>>(
    () => (mobilePlanATripCard ? { Header: () => mobilePlanATripCard } : {}),
    [mobilePlanATripCard]
  );

  const { maxContentHeight } = useSidebarContext();
  return (
    <>
      {isMobile ? (
        <>
          {isLoading && <SidebarLoading />}
          {!isLoading && !resultsWithPhotos?.length && (
            <>
              {mobilePlanATripCard}
              {NullState}
            </>
          )}
          {!isLoading && !!resultsWithPhotos?.length && (
            <Virtuoso
              atTopStateChange={atTop => dispatch(setIsMobileListAtTop(atTop))}
              components={mobileVirtuosoComponents}
              data={resultsWithPhotos}
              itemContent={renderResultCard}
            />
          )}
        </>
      ) : (
        <div className={styles.container}>
          {SidebarHeader(<FormattedMessage defaultMessage="Explore trails" />)}
          {SidebarContent(
            isLoading ? (
              // Fill the content area so the panel keeps a stable height while loading (instead of collapsing to
              // a small spinner and jumping when results render). Matches the route-level loading skeleton.
              <div style={{ height: maxContentHeight > 0 ? `${maxContentHeight}px` : undefined }}>
                <SidebarLoading />
              </div>
            ) : (
              <>
                <div ref={searchInfoRef} className={classNames(styles.subtitleArea, { [styles.scrollShadow]: !isAtTop })}>
                  {isTripPlannerEnabled && <PlanATripCard entryPoint="explore side bar" />}
                  <div className={styles.summaryAndSort}>
                    <div>{searchResultsSummary?.displayText}</div>
                    <SortDropdown />
                  </div>
                  {isSeasonalSort && <ExploreSeasonalMonthSelector selectedMonth={seasonalMonth} />}
                </div>
                {resultsWithPhotos.length === 0 && <div className={styles.contentContainer}>{NullState}</div>}
                {!!resultsWithPhotos.length && (
                  <Virtuoso
                    atTopStateChange={setIsAtTop}
                    className={styles.virtuosoList}
                    style={{ height: `${maxContentHeight - (searchInfoRef.current?.clientHeight ?? 0)}px` }}
                    data={resultsWithPhotos}
                    itemContent={renderResultCard}
                    onScroll={onScroll}
                  />
                )}
              </>
            )
          )}
        </div>
      )}
      {listModal}
      {signUpModal}
    </>
  );
};

export default ExploreSidebar;
